import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import useParkingService from '../hooks/parking-services/useParkingService.ts';
import {
  RateDetail,
  RegularRule,
  SpecificRule,
} from '../models/ParkingRate.ts';
import { IconChevronLeft } from '@tabler/icons-react';
import { useTheme } from '../context/ThemeProvider.tsx';
import { DOW_ZH, dateToShortLabel } from '../utils/ParkingRateCardUtils.ts';
import {
  useParkingRateGroups,
  uid,
  type RateGroup,
} from '../hooks/parking-services/useParkingRateGroups.ts';
import { useParkingRatePreview } from '../hooks/parking-services/useParkingRatePreview.ts';
import { useParkingMaxFeeGroups } from '../hooks/parking-services/useParkingMaxFeeGroups.ts';
import { useParkingRateForm } from '../hooks/parking-services/useParkingRateForm.ts';
import { useSaveParkingRates } from '../hooks/parking-services/useSaveParkingRates.ts';
import { getDay, parseISO } from 'date-fns';
import {
  DAYS,
  DAY_SHORT,
  dateToStr,
  getDatesInRange,
  computeDowSubset,
} from '../utils/ParkingRateTime.ts';
import {
  regularRuleToGroups,
  specificRuleToGroups,
  regularRuleToMaxFeeEntries,
  specificRuleToMaxFeeEntries,
} from '../utils/ParkingRateConverters.ts';
import ParkingWeekGrid from '../components/ui/places/ParkingWeekGrid.tsx';
import ParkingRateGroupCard from '../components/ui/places/ParkingRateGroupCard.tsx';
import MaxFeeRuleListEditor from '../components/ui/places/MaxFeeRuleListEditor.tsx';
import ParkingRatePreviewCard from '../components/ui/places/ParkingRatePreviewCard.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

type LocalRuleType = 'regular' | 'specific';

// ─── Page component ───────────────────────────────────────────────────────────
interface ParkingRatePageProps {
  isEditMode: boolean;
  ruleTypeParam?: 'regular' | 'specific';
  ruleIndex?: number;
  /** Edit mode only: guaranteed defined on mount (parent gates on service load). */
  initialRule?: RegularRule | SpecificRule;
}

const ParkingRatePage: React.FC<ParkingRatePageProps> = ({
  isEditMode,
  ruleTypeParam,
  ruleIndex,
  initialRule,
}) => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const previousPagePlaceId = (location.state as { placeId?: string } | null)
    ?.placeId;
  const copyRule = (
    location.state as { copyRule?: RegularRule | SpecificRule } | null
  )?.copyRule;
  const copyRuleType = (
    location.state as { copyRuleType?: 'regular' | 'specific' } | null
  )?.copyRuleType;

  const { data: service } = useParkingService(serviceId!);

  // ── Stepper ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Step 1 state（form + 生效期間衍生值 + 日期驗證，custom hook）──────────
  const {
    form,
    localRuleType,
    setLocalRuleType,
    regularComputedEndDate,
    regularDowSubset,
    specialDates,
    overlapError,
  } = useParkingRateForm({
    service,
    initialRule,
    isEditMode,
    ruleTypeParam,
    ruleIndex,
  });
  const {
    name,
    startDate,
    endDate,
    description,
    entryBufferTime,
    isResetEveryDay,
  } = form.values;
  const [specificCopyTemplate, setSpecificCopyTemplate] = useState<
    RateGroup[] | null
  >(null);

  // ── Groups, drag, and CRUD (custom hook) ─────────────────────────────────
  const [initialGroups] = useState<RateGroup[]>(() => {
    if (!initialRule) return [];
    const s = initialRule.effectivePeriod.startDateTime.split('T')[0];
    const e = initialRule.effectivePeriod.endDateTime.split('T')[0];
    return ruleTypeParam === 'regular'
      ? regularRuleToGroups(initialRule as RegularRule, computeDowSubset(s, e))
      : specificRuleToGroups(initialRule as SpecificRule);
  });

  const {
    groups,
    setGroups,
    editingGroupId,
    dragRect,
    displayGroups,
    startEditGroup,
    addNewGroup,
    deleteGroup,
    confirmGroupRate,
    cancelGroupEdit,
    clearAll,
    handleCellDown,
    handleCellEnter,
  } = useParkingRateGroups<RateDetail>({
    localRuleType,
    regularDowSubset,
    specialDates,
    initialGroups,
  });

  // ── MaxFee list state (custom hook) ────────────────────────────────────────
  const { maxFeeEntries, setMaxFeeEntries } = useParkingMaxFeeGroups({
    initialRule,
    ruleTypeParam,
  });

  // Step 2: scroll the 最高收費規則 list back to top when a new rule card is
  // prepended, so the user notices it instead of it silently landing off-screen.
  const maxFeeListBodyRef = useRef<HTMLDivElement>(null);

  // Step 2: 「規則」為必選——按下一步當下拍一張「哪些項目還是空的」快照。
  // 用快照而不是即時重算，是為了讓按下一步「之後」才新增的卡片不會被牽連一起亮紅；
  // 每個 Select 是否顯示紅框仍會另外檢查 mode 是否還是空的，所以補選後照樣會自動消紅。
  const [invalidFeeItemKeys, setInvalidFeeItemKeys] = useState<Set<string>>(
    new Set(),
  );

  // Populate form once when entering add mode with a copied rule
  // Step 1: set metadata (and dates for regular rules)
  const hasCopyInitialized = useRef(false);
  useEffect(() => {
    if (hasCopyInitialized.current || isEditMode || !copyRule || !copyRuleType)
      return;
    hasCopyInitialized.current = true;
    form.setValues({
      name: copyRule.name,
      description: copyRule.description ?? '',
      entryBufferTime:
        copyRule.additionalRules?.entryBufferTime?.toString() ?? '',
      isResetEveryDay: copyRule.additionalRules?.isResetEveryDay ?? false,
    });
    setLocalRuleType(copyRuleType as LocalRuleType);
    if (copyRuleType === 'regular') {
      // Dates stay empty; user picks a new range, then hasCopyGroupsInitialized
      // fires and maps the copied groups onto the new regularDowSubset.
    } else if (copyRuleType === 'specific') {
      // Extract the first day's blocks as a template; dates stay null so the
      // user picks a new range, then the third useEffect expands the template
      // to every day in that range.
      const allGroups = specificRuleToGroups(copyRule as SpecificRule);
      const template = allGroups
        .map((group) => ({
          ...group,
          blocks: group.blocks
            .filter((block) => block.days.includes(0))
            .map((block) => ({ ...block, days: [0] })),
        }))
        .filter((group) => group.blocks.length > 0);
      setSpecificCopyTemplate(template);
    }
  }, [copyRule, copyRuleType, form, isEditMode, setLocalRuleType]);

  // Step 2 (regular only): populate groups once regularDowSubset is ready
  const hasCopyGroupsInitialized = useRef(false);
  useEffect(() => {
    if (
      hasCopyGroupsInitialized.current ||
      isEditMode ||
      !copyRule ||
      copyRuleType !== 'regular' ||
      !startDate
    )
      return;
    hasCopyGroupsInitialized.current = true;
    setGroups(regularRuleToGroups(copyRule as RegularRule, regularDowSubset));
    setMaxFeeEntries(regularRuleToMaxFeeEntries(copyRule as RegularRule));
  }, [
    isEditMode,
    copyRule,
    copyRuleType,
    startDate,
    regularDowSubset,
    setGroups,
    setMaxFeeEntries,
  ]);

  // Step 3 (specific only): once the user picks a date range, expand the
  // first-day template to cover every day in the new range
  const hasCopySpecificGroupsInitialized = useRef(false);
  useEffect(() => {
    if (
      hasCopySpecificGroupsInitialized.current ||
      isEditMode ||
      !copyRule ||
      copyRuleType !== 'specific' ||
      !specificCopyTemplate ||
      !startDate ||
      !endDate
    )
      return;
    const numDays = getDatesInRange(
      dateToStr(startDate),
      dateToStr(endDate),
    ).length;
    if (numDays === 0) return; // 起始日晚於結束日（無效區間），不消耗 ref
    hasCopySpecificGroupsInitialized.current = true;
    const expandedGroups = specificCopyTemplate.map((group) => ({
      ...group,
      blocks: group.blocks.map((block) => ({
        ...block,
        days: Array.from({ length: numDays }, (_, i) => i),
      })),
    }));
    setGroups(expandedGroups);

    // 最高收費：比照費率，取複製來源「第一天」的樣式，套用到新區間每一天
    const copyStart = copyRule.effectivePeriod.startDateTime.split('T')[0];
    const copyEnd = copyRule.effectivePeriod.endDateTime.split('T')[0];
    const copyDates = getDatesInRange(copyStart, copyEnd);
    const expandedMaxFee = specificRuleToMaxFeeEntries(
      copyRule as SpecificRule,
      copyDates,
    )
      .filter((e) => e.checkedDays.includes(0) && e.feeItems.length > 0)
      .map((e) => {
        const firstDaySlots = e.daySlots[0] ?? [];
        const daySlots: Record<number, typeof firstDaySlots> = {};
        for (let i = 0; i < numDays; i++) {
          daySlots[i] = firstDaySlots.map((s) => ({ ...s, id: uid() }));
        }
        return {
          id: uid(),
          checkedDays: Array.from({ length: numDays }, (_, i) => i),
          daySlots,
          feeItems: e.feeItems,
        };
      });
    if (expandedMaxFee.length > 0) setMaxFeeEntries(expandedMaxFee);
  }, [
    isEditMode,
    copyRule,
    copyRuleType,
    specificCopyTemplate,
    startDate,
    endDate,
    setGroups,
    setMaxFeeEntries,
  ]);

  // ── Step 2 費率預覽資料（custom hook）─────────────────────────────────────
  const {
    specialColumnLabels,
    previewDayCards,
    previewDateCards,
    previewMaxFeeDayCards,
    previewMaxFeeDateCards,
  } = useParkingRatePreview({
    localRuleType,
    startDate,
    endDate,
    regularComputedEndDate,
    name,
    groups,
    regularDowSubset,
    specialDates,
    maxFeeEntries,
  });

  // ── Step 1「預覽費率」按鈕：跟 Step 3 用同一份轉換邏輯，但 maxFeeEntries 固定給
  // 空陣列——Step 1 這時還沒有最高收費資料，即使使用者正在編輯一筆本來就已經設定過
  // 最高收費的既有規則（此時 Step 2 的 maxFeeEntries state 其實已經有值），這裡也要
  // 強制不顯示最高收費區塊。
  const [step1PreviewOpened, step1PreviewHandlers] = useDisclosure(false);
  const {
    previewDayCards: step1PreviewDayCards,
    previewDateCards: step1PreviewDateCards,
  } = useParkingRatePreview({
    localRuleType,
    startDate,
    endDate,
    regularComputedEndDate,
    name,
    groups,
    regularDowSubset,
    specialDates,
    maxFeeEntries: [],
  });

  // ── Navigation ───────────────────────────────────────────────────────────
  const navigateBack = (withReopen = true) => {
    if (previousPagePlaceId) {
      navigate(`/places/${previousPagePlaceId}`, {
        state: withReopen ? { openRateOverview: true, serviceId } : {},
        replace: true,
      });
    } else {
      navigate(-1);
    }
  };

  // ── Step 1 validation（下一步按鈕）────────────────────────────────────
  const handleNextStep = () => {
    const result = form.validate();
    if (result.hasErrors) return;
    if (localRuleType === 'specific' && !endDate) {
      form.setFieldError('endDate', ' ');
      return;
    }
    if (overlapError) return;
    setStep(2);
  };

  // ── Save（custom hook）────────────────────────────────────────────────────
  const { saveAll, isSaving } = useSaveParkingRates({
    service,
    serviceId,
    localRuleType,
    name,
    startDate,
    endDate,
    description,
    entryBufferTime,
    isResetEveryDay,
    groups,
    regularDowSubset,
    specialDates,
    maxFeeEntries,
    ruleIndex,
    isEditMode,
    navigateBack,
  });

  // ── Derived ──────────────────────────────────────────────────────────────
  const isSpecial = localRuleType === 'specific';
  const numDays = isSpecial ? specialDates.length : regularDowSubset.length;
  /** 日期已填且無驗證錯誤時，允許 WeekGrid 互動 */
  const isDatesValid = isSpecial
    ? !!startDate && !!endDate && !overlapError
    : !!startDate && !overlapError;
  const colLabels = isSpecial
    ? specialColumnLabels
    : regularDowSubset.length < 7
      ? regularDowSubset.map((i) => DAYS[i]) // e.g. ['週三', '週四', '週五']
      : undefined;
  const pageTitle = isEditMode ? '編輯費率' : '新增費率';
  const serviceName = service?.name ?? '';

  const { colorScheme } = useTheme();

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minWidth: 1146,
        display: 'flex',
        flexDirection: 'column',
        height: '94vh',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        fontSize: 14,
        color: '#1a1a1a',
        position: 'relative',
      }}
    >
      {/* ── Header（固定，不隨內容捲動） ─────────────────────────────────── */}
      <Stack
        gap={12}
        style={{ alignItems: 'start', paddingInline: 0, flexShrink: 0 }}
      >
        <Button
          variant={'transparent'}
          leftSection={<IconChevronLeft size={16} />}
          onClick={() => navigateBack(true)}
          style={{ color: 'grey', height: 32, fontSize: 14, fontWeight: 400 }}
          styles={{ root: { paddingInline: 0 } }}
        >
          返回費率管理
        </Button>

        <Card
          shadow={'xs'}
          style={{
            paddingInline: 30,
            height: 108,
            width: '100%',
            backgroundColor:
              colorScheme === 'light' ? 'white' : 'var(--mantine-color-body)',
            borderRadius: 16,
            marginBottom: 16,
          }}
        >
          <Stack gap={12}>
            <Text style={{ fontSize: 20, fontWeight: 700 }}>
              {pageTitle}
              {serviceName ? `｜ ${serviceName}` : ''}
            </Text>
            <Stepper active={step - 1} size="xs" style={{ width: 450 }}>
              <Stepper.Step label="基本費率設定" />
              <Stepper.Step label="最高收費設定" />
              <Stepper.Step label="其餘費率設定" />
            </Stepper>
          </Stack>
        </Card>
      </Stack>

      {/* ── Content（flex: 1，兩個 Step 都填滿不捲動） ───────────────────── */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* ── Step 1：compact 表單（上）+ WeekGrid / GroupCard（下） ────── */}
        {step === 1 && (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* 上方：費率形式 + 費率名稱 + 日期 */}
            <Card
              shadow={'xs'}
              style={{
                backgroundColor:
                  colorScheme === 'light'
                    ? 'white'
                    : 'var(--mantine-color-body)',
                borderRadius: 16,
                padding: '16px 24px',
                flexShrink: 0,
                marginBottom: 16,
              }}
            >
              <Group align="flex-start" gap={24} style={{ flex: 1 }}>
                <Select
                  label="費率形式"
                  withAsterisk
                  value={localRuleType}
                  data={[
                    { value: 'regular', label: '一般費率' },
                    { value: 'specific', label: '特殊費率' },
                  ]}
                  onChange={(v) => {
                    if (v && v !== localRuleType) {
                      clearAll();
                      setLocalRuleType(v as LocalRuleType);
                      if (!isEditMode) {
                        const label = v === 'regular' ? '一般費率' : '特殊費率';
                        const currentName = form.values.name;
                        if (
                          currentName === '' ||
                          currentName === '一般費率' ||
                          currentName === '特殊費率'
                        ) {
                          form.setFieldValue('name', label);
                        }
                      }
                    }
                  }}
                  style={{ flex: 1, minWidth: 100 }}
                />
                <TextInput
                  label="費率名稱"
                  withAsterisk
                  placeholder="請輸入費率名稱"
                  {...form.getInputProps('name')}
                  style={{ flex: 1, minWidth: 0 }}
                />
                {/* 日期欄位：內部用 column 排列，error 文字獨立放於下方 */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {localRuleType === 'regular' ? (
                    <>
                      <DatePickerInput
                        label="費率生效起始日"
                        withAsterisk
                        placeholder="起始日"
                        valueFormat="YYYY/MM/DD"
                        value={startDate}
                        error={!!form.errors.startDate || !!overlapError}
                        onChange={(v) => {
                          form.setFieldValue(
                            'startDate',
                            v ? new Date(v) : null,
                          );
                        }}
                        style={{ minWidth: 0 }}
                      />
                      {overlapError ? (
                        <Text
                          size="xs"
                          c="red"
                          style={{ whiteSpace: 'nowrap', marginTop: 4 }}
                        >
                          {overlapError}
                        </Text>
                      ) : regularComputedEndDate && startDate ? (
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{ whiteSpace: 'nowrap', marginTop: 4 }}
                        >
                          生效期間：{dateToStr(startDate).replace(/-/g, '/')} –{' '}
                          {regularComputedEndDate.replace(/-/g, '/')}
                        </Text>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <Group align="flex-end" wrap="nowrap" gap={12}>
                        <DatePickerInput
                          label="費率生效起始日"
                          withAsterisk
                          placeholder="起始日"
                          valueFormat="YYYY/MM/DD"
                          value={startDate}
                          error={!!form.errors.startDate || !!overlapError}
                          onChange={(v) => {
                            form.setFieldValue(
                              'startDate',
                              v ? new Date(v) : null,
                            );
                          }}
                          style={{ flex: 1, minWidth: 0 }}
                        />
                        <Text mb={6} c="dimmed" style={{ flexShrink: 0 }}>
                          →
                        </Text>
                        <DatePickerInput
                          label="費率生效結束日"
                          withAsterisk
                          placeholder="結束日"
                          valueFormat="YYYY/MM/DD"
                          value={endDate}
                          error={!!form.errors.endDate || !!overlapError}
                          onChange={(v) => {
                            form.setFieldValue(
                              'endDate',
                              v ? new Date(v) : null,
                            );
                          }}
                          style={{ flex: 1, minWidth: 0 }}
                        />
                      </Group>
                      {overlapError && (
                        <Text
                          size="xs"
                          c="red"
                          style={{ whiteSpace: 'nowrap', marginTop: 4 }}
                        >
                          {overlapError}
                        </Text>
                      )}
                    </>
                  )}
                </div>
              </Group>
            </Card>

            {/* 下方：WeekGrid + GroupCard（日期合法時才顯示） */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                overflow: 'hidden',
                paddingBottom: 4,
              }}
            >
              {!isDatesValid ? (
                /* 日期尚未填寫或有錯誤：顯示 placeholder，不允許 WeekGrid 互動 */
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                    fontSize: 14,
                    textAlign: 'center',
                    padding: 32,
                  }}
                >
                  {isSpecial
                    ? !startDate || !endDate
                      ? '請先設定費率生效起始日與結束日'
                      : '請修正日期錯誤後再設定費率時段'
                    : !startDate
                      ? '請先設定費率生效起始日'
                      : '請修正日期錯誤後再設定費率時段'}
                </div>
              ) : (
                <>
                  {/* Left: GroupCard list */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      marginRight: 16,
                      overflow: 'hidden',
                    }}
                  >
                    <Card
                      shadow={'xs'}
                      style={{
                        padding: 0,
                        borderRadius: 12,
                        width: 316,
                        flex: 1,
                        flexShrink: 0,
                        background:
                          colorScheme === 'light'
                            ? 'white'
                            : 'var(--mantine-color-body)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        marginBottom: 2,
                        marginRight: 2,
                      }}
                    >
                      <div
                        style={{
                          padding: '16px 18px 14px',
                          borderBottom:
                            colorScheme === 'light'
                              ? '1px solid #f3f4f6'
                              : '1px solid #737373ff',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                          基本費率時段列表
                        </div>
                        <button
                          onClick={addNewGroup}
                          style={{
                            padding: '6px 14px',
                            border: 'none',
                            borderRadius: 8,
                            background: '#2563eb',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          + 新增
                        </button>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          overflowY: 'auto',
                          padding: '12px 14px',
                        }}
                      >
                        {displayGroups.length === 0 ? (
                          <div
                            style={{
                              textAlign: 'center',
                              padding: '48px 16px',
                              color: '#9ca3af',
                              fontSize: 12,
                              lineHeight: 1.8,
                            }}
                          >
                            <div style={{ fontSize: 32, marginBottom: 10 }}>
                              ⏱
                            </div>
                            尚未設定任何費率時段
                            <br />
                            點擊「+ 新增」建立費率組
                            <br />
                            再在左側格子拖曳選取時段
                          </div>
                        ) : (
                          [...displayGroups]
                            .reverse()
                            .map((g) => (
                              <ParkingRateGroupCard
                                key={g.id}
                                group={g}
                                columnLabels={colLabels}
                                isEditing={g.id === editingGroupId}
                                onEdit={startEditGroup}
                                onConfirm={confirmGroupRate}
                                onCancel={cancelGroupEdit}
                                onDelete={deleteGroup}
                              />
                            ))
                        )}
                      </div>
                    </Card>
                  </div>

                  {/* Right: grid */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <Card
                      shadow={'xs'}
                      style={{
                        padding: 0,
                        flex: 1,
                        minHeight: 0,
                        background:
                          colorScheme === 'light'
                            ? 'white'
                            : 'var(--mantine-color-body)',
                        borderRadius: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        marginBottom: 2,
                      }}
                    >
                      <Group
                        gap={16}
                        justify="space-between"
                        style={{
                          padding: '16px 20px',
                          borderBottom:
                            colorScheme === 'light'
                              ? '1px solid #f3f4f6'
                              : '1px solid #737373ff',
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          {editingGroupId !== null
                            ? '在格子上拖曳選取時段，加入到目前編輯的費率組'
                            : isSpecial && numDays > 0
                              ? `${dateToStr(startDate!)} – ${dateToStr(endDate!)}（共 ${numDays} 天）`
                              : isSpecial
                                ? '請先設定上方有效日期區間'
                                : '請先在左側點擊「+ 新增」建立費率組，再拖曳選取時段'}
                        </div>
                        <Button
                          variant="light"
                          size="xs"
                          onClick={step1PreviewHandlers.open}
                        >
                          預覽費率
                        </Button>
                      </Group>
                      <div
                        style={{
                          flex: 1,
                          minHeight: 0,
                          overflow: 'auto',
                          paddingInline: '20px',
                          paddingBottom: '20px',
                          marginTop: 12,
                        }}
                      >
                        {isSpecial && numDays === 0 ? (
                          <div
                            style={{
                              textAlign: 'center',
                              color: '#9ca3af',
                              padding: 32,
                              fontSize: 13,
                            }}
                          >
                            請先設定上方有效日期區間
                          </div>
                        ) : (
                          <ParkingWeekGrid
                            numDays={numDays}
                            groups={displayGroups}
                            editingGroupId={editingGroupId}
                            dragRect={dragRect}
                            columnLabels={colLabels}
                            onCellDown={handleCellDown}
                            onCellEnter={handleCellEnter}
                          />
                        )}
                      </div>
                    </Card>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Step 2：最高收費設定 ────────────────────────────────────────── */}
        {step === 2 && (
          <div
            style={{
              height: '100%',
              display: 'flex',
              overflow: 'hidden',
              paddingBottom: 4,
            }}
          >
            {!isDatesValid ? (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9ca3af',
                  fontSize: 14,
                  textAlign: 'center',
                  padding: 32,
                }}
              >
                {isSpecial
                  ? !startDate || !endDate
                    ? '請先設定費率生效起始日與結束日'
                    : '請修正日期錯誤後再設定費率時段'
                  : !startDate
                    ? '請先設定費率生效起始日'
                    : '請修正日期錯誤後再設定費率時段'}
              </div>
            ) : (
              <>
                {/* ── Left: 最高收費規則列表 ── */}
                <Card
                  shadow="xs"
                  style={{
                    padding: 0,
                    borderRadius: 12,
                    flex: '0 0 auto',
                    width: 814,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    marginRight: 16,
                    marginBottom: 2,
                    background:
                      colorScheme === 'light'
                        ? 'white'
                        : 'var(--mantine-color-body)',
                  }}
                >
                  {/* card header */}
                  <div
                    style={{
                      padding: '14px 18px',
                      borderBottom:
                        colorScheme === 'light'
                          ? '1px solid #f3f4f6'
                          : '1px solid #737373ff',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                      最高收費規則
                    </div>
                    <button
                      onClick={() => {
                        setMaxFeeEntries((prev) => [
                          {
                            id: uid(),
                            checkedDays: [],
                            daySlots: {},
                            feeItems: [{ mode: '', fee: '', interval: '' }],
                          },
                          ...prev,
                        ]);
                        requestAnimationFrame(() => {
                          maxFeeListBodyRef.current?.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                          });
                        });
                      }}
                      style={{
                        padding: '6px 14px',
                        border: 'none',
                        borderRadius: 8,
                        background: '#2563eb',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      + 新增
                    </button>
                  </div>

                  {/* list body */}
                  <div
                    ref={maxFeeListBodyRef}
                    style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}
                  >
                    <MaxFeeRuleListEditor
                      entries={maxFeeEntries}
                      onChange={setMaxFeeEntries}
                      isSpecific={isSpecial}
                      dayLabels={
                        isSpecial
                          ? specialDates.map(
                              (d) =>
                                `${dateToShortLabel(d)}(${DOW_ZH[getDay(parseISO(d))]})`,
                            )
                          : DAY_SHORT
                      }
                      colorScheme={colorScheme === 'dark' ? 'dark' : 'light'}
                      invalidFeeItemKeys={invalidFeeItemKeys}
                    />
                  </div>
                </Card>

                {/* ── Right: 預覽 ── */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Card
                    shadow="xs"
                    style={{
                      padding: 0,
                      flex: 1,
                      minHeight: 0,
                      borderRadius: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      marginBottom: 2,
                      background:
                        colorScheme === 'light'
                          ? 'white'
                          : 'var(--mantine-color-body)',
                    }}
                  >
                    <div
                      style={{
                        padding: '14px 20px',
                        borderBottom:
                          colorScheme === 'light'
                            ? '1px solid #f3f4f6'
                            : '1px solid #737373ff',
                        fontSize: 14,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      預覽
                    </div>
                    <div
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '14px 20px',
                      }}
                    >
                      <ParkingRatePreviewCard
                        ruleType={localRuleType}
                        name={name}
                        startDate={startDate}
                        endDate={
                          localRuleType === 'regular' && regularComputedEndDate
                            ? parseISO(regularComputedEndDate)
                            : endDate
                        }
                        dayCards={previewMaxFeeDayCards}
                        dateCards={previewMaxFeeDateCards}
                        itemPerRow={1}
                        isShowRatePeriods={false}
                      />
                    </div>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 1「預覽費率」彈窗：跟 Step 3 的費率預覽同一顆元件、同一組 props，
            只是資料來源改用不含最高收費的 step1PreviewDayCards/step1PreviewDateCards */}
        <Modal
          opened={step1PreviewOpened}
          onClose={step1PreviewHandlers.close}
          title="費率預覽"
          centered
          size="60%"
          styles={{
            title: { fontWeight: 700, fontSize: 16 },
            content: { minWidth: 1040 },
          }}
        >
          <ParkingRatePreviewCard
            ruleType={localRuleType}
            name={name}
            startDate={startDate}
            endDate={
              localRuleType === 'regular' && regularComputedEndDate
                ? parseISO(regularComputedEndDate)
                : endDate
            }
            dayCards={step1PreviewDayCards}
            dateCards={step1PreviewDateCards}
            itemPerRow={3}
            isShowRatePeriods={true}
          />
        </Modal>

        {/* ── Step 3：費率預覽 + 其餘費率設定 ─────────────────────────────── */}
        {step === 3 && (
          <div style={{ height: '100%', overflowY: 'auto' }}>
            {/* 費率預覽 */}
            <Card
              shadow={'xs'}
              style={{
                padding: 0,
                background:
                  colorScheme === 'light'
                    ? 'white'
                    : 'var(--mantine-color-body)',
                borderRadius: 12,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom:
                    colorScheme === 'light'
                      ? '1px solid #f3f4f6'
                      : '1px solid #737373ff',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                費率預覽
              </div>
              <div style={{ padding: '16px 20px' }}>
                <ParkingRatePreviewCard
                  ruleType={localRuleType}
                  name={name}
                  startDate={startDate}
                  endDate={
                    localRuleType === 'regular' && regularComputedEndDate
                      ? parseISO(regularComputedEndDate)
                      : endDate
                  }
                  dayCards={previewDayCards}
                  dateCards={previewDateCards}
                  itemPerRow={3}
                  isShowRatePeriods={true}
                />
              </div>
            </Card>

            {/* 其餘費率設定 */}
            <Card
              shadow={'xs'}
              style={{
                backgroundColor:
                  colorScheme === 'light'
                    ? 'white'
                    : 'var(--mantine-color-body)',
                borderRadius: 12,
                marginBottom: 2,
                padding: 0,
              }}
            >
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom:
                    colorScheme === 'light'
                      ? '1px solid #f3f4f6'
                      : '1px solid #737373ff',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                其他資訊
              </div>
              <Group gap={48} style={{ padding: '8px 20px' }}>
                <Select
                  label="跨日重新計算"
                  data={[
                    { value: 'false', label: '否' },
                    { value: 'true', label: '是' },
                  ]}
                  value={isResetEveryDay ? 'true' : 'false'}
                  onChange={(v) =>
                    form.setFieldValue('isResetEveryDay', v === 'true')
                  }
                  allowDeselect={false}
                  styles={{ label: { fontWeight: 700 } }}
                />
                <NumberInput
                  mt={12}
                  label="進場緩衝免費時間（分鐘）"
                  placeholder="若無緩衝時間則不需填寫"
                  value={entryBufferTime === '' ? '' : Number(entryBufferTime)}
                  onChange={(v) =>
                    form.setFieldValue(
                      'entryBufferTime',
                      v === '' ? '' : String(v),
                    )
                  }
                  min={0}
                  allowDecimal={false}
                  styles={{ input: { width: 240 } }}
                />
              </Group>
              <Textarea
                label="費率備註"
                placeholder="請輸入費率備註說明"
                {...form.getInputProps('description')}
                minRows={4}
                autosize
                style={{ paddingInline: 20, paddingBottom: 16 }}
              />
            </Card>
          </div>
        )}
      </div>

      {/* ── Footer（固定，不隨內容捲動） ─────────────────────────────────── */}
      <Card
        shadow={'xs'}
        style={{
          paddingInline: 30,
          height: 68,
          width: '100%',
          backgroundColor:
            colorScheme === 'light' ? 'white' : 'var(--mantine-color-body)',
          borderRadius: 16,
          flexShrink: 0,
          marginTop: 16,
          marginBottom: 2,
        }}
      >
        {step === 1 ? (
          <Group style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button variant="default" onClick={() => navigateBack(true)}>
              取消
            </Button>
            <Group gap={8}>
              <Button variant="default" onClick={clearAll}>
                清除費率
              </Button>
              <Button onClick={handleNextStep}>下一步</Button>
            </Group>
          </Group>
        ) : step === 2 ? (
          <Group style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button variant="default" onClick={() => setStep(1)}>
              上一步
            </Button>
            <Group gap={8}>
              <Button
                variant="default"
                onClick={() => {
                  setMaxFeeEntries([]);
                  setInvalidFeeItemKeys(new Set());
                }}
              >
                清除最高收費
              </Button>
              <Button
                onClick={() => {
                  const keys = new Set<string>();
                  maxFeeEntries.forEach((entry) => {
                    entry.feeItems.forEach((item, feeIdx) => {
                      if (item.mode === '') keys.add(`${entry.id}-${feeIdx}`);
                    });
                  });
                  if (keys.size > 0) {
                    setInvalidFeeItemKeys(keys);
                    return;
                  }
                  setInvalidFeeItemKeys(new Set());
                  setStep(3);
                }}
              >
                下一步
              </Button>
            </Group>
          </Group>
        ) : (
          <Group style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button variant="default" onClick={() => setStep(2)}>
              上一步
            </Button>
            <Button loading={isSaving} onClick={saveAll}>
              儲存設定
            </Button>
          </Group>
        )}
      </Card>
    </div>
  );
};

export default ParkingRatePage;
